import {
  ApartmentOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  StarFilled,
  StarOutlined,
  TagOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../api/errors';
import { listProjectTypes } from '../api/projects';
import type { ProjectType } from '../api/projects';
import {
  createProjectTemplate,
  incrementProjectTemplateVersion,
  listProjectTemplates,
  setProjectTemplateActive,
  setProjectTemplateDefault,
  updateProjectTemplate,
} from '../api/projectTemplates';
import type { ProjectTemplate } from '../api/projectTemplates';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';
import { BrandColors } from '../theme/brand';

/** Giá trị của biểu mẫu khai và sửa mẫu. */
interface TemplateFormValues {
  readonly code: string;
  readonly name: string;
  readonly projectTypeId: string;
  readonly description: string | null;
  readonly estimatedDurationMonths: number | null;
}

/** Dữ liệu đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedTemplates {
  readonly key: string;
  readonly items: readonly ProjectTemplate[];
  readonly projectTypes: readonly ProjectType[];
}

/**
 * Số loại dự án nạp về cho ô chọn.
 *
 * Số loại dự án của một công ty là số dòng sản phẩm của công ty đó — vài chục là nhiều. Lấy một
 * trang lớn hơn con số đó rồi thôi, thay vì dựng ô chọn có tìm kiếm phía máy chủ cho một danh mục
 * không bao giờ dài.
 */
const projectTypePageSize = 200;

/**
 * Trang mẫu dự án.
 *
 * <b>Mẫu dự án trả lời một câu hỏi rất cụ thể:</b> mở một dự án AOI mới thì cây công việc chuẩn gồm
 * những gì. Không có mẫu thì mỗi người quản lý dựng lại từ đầu, và hai dự án cùng loại có hai cây
 * công việc khác nhau — lúc đó không so sánh được dự án nào chậm hơn dự án nào.
 *
 * <b>Không có nút xóa mẫu.</b> Mẫu đã áp cho dự án nào thì con số của những dự án đó sinh ra từ nó,
 * nên phải còn để tra lại. Thứ thay thế là ngừng dùng: mẫu ngừng dùng không hiện ra lúc áp nữa.
 *
 * <b>Số phiên bản do người khai mẫu tự tăng.</b> Sửa một lỗi chính tả trong tên việc không phải một
 * phiên bản mới của quy trình; đổi thứ tự các bước thì mới là. Máy không phân biệt được hai việc đó,
 * nên nó không đoán.
 */
export function ProjectTemplatePage(): ReactNode {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const canManage = hasPermission(PermissionCodes.ProjectTemplateManage);

  const [form] = Form.useForm<TemplateFormValues>();

  const [projectTypeFilter, setProjectTypeFilter] = useState<string | null>(null);
  const [onlyActive, setOnlyActive] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedTemplates | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editing, setEditing] = useState<ProjectTemplate | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const requestKey = useMemo(
    () => JSON.stringify({ projectTypeFilter, onlyActive, reloadToken }),
    [projectTypeFilter, onlyActive, reloadToken],
  );

  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        /*
          Hai lời gọi song song: danh sách mẫu và danh mục loại dự án. Loại dự án cần cho ô lọc và cho
          biểu mẫu khai mẫu mới, nên nạp một lần cùng lúc thay vì đợi người dùng mở biểu mẫu rồi mới
          nạp — lúc đó biểu mẫu hiện ra với ô chọn rỗng trong một nhịp.
        */
        const [templates, projectTypes] = await Promise.all([
          listProjectTemplates({
            projectTypeId: projectTypeFilter ?? undefined,
            onlyActive: onlyActive ? true : undefined,
          }),
          listProjectTypes({ pageNumber: 1, pageSize: projectTypePageSize }),
        ]);

        if (!isCurrent) {
          return;
        }

        setLoaded({ key: requestKey, items: templates, projectTypes: projectTypes.items });
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được danh sách mẫu dự án.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [requestKey, projectTypeFilter, onlyActive]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  function openCreate(): void {
    setEditing(null);
    form.resetFields();
    setIsFormOpen(true);
  }

  function openEdit(template: ProjectTemplate): void {
    setEditing(template);
    form.setFieldsValue({
      code: template.code,
      name: template.name,
      projectTypeId: template.projectTypeId,
      description: template.description,
      estimatedDurationMonths: template.estimatedDurationMonths,
    });
    setIsFormOpen(true);
  }

  async function submit(values: TemplateFormValues): Promise<void> {
    setIsSaving(true);

    try {
      const payload = {
        code: values.code,
        name: values.name,
        projectTypeId: values.projectTypeId,
        description: values.description ?? null,
        estimatedDurationMonths: values.estimatedDurationMonths ?? null,
      };

      if (editing === null) {
        await createProjectTemplate(payload);
        message.success('Đã khai mẫu dự án mới. Bước tiếp theo là dựng cây công việc cho nó.');
      } else {
        await updateProjectTemplate(editing.id, payload);
        message.success('Đã lưu thay đổi.');
      }

      setIsFormOpen(false);
      form.resetFields();
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được.');
    } finally {
      setIsSaving(false);
    }
  }

  async function toggleDefault(template: ProjectTemplate): Promise<void> {
    try {
      await setProjectTemplateDefault(template.id, !template.isDefault);

      message.success(
        template.isDefault ? 'Đã bỏ mẫu mặc định.' : 'Đã đặt làm mẫu mặc định của loại dự án này.',
      );
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không đổi được.');
    }
  }

  async function toggleActive(template: ProjectTemplate): Promise<void> {
    try {
      await setProjectTemplateActive(template.id, !template.isActive);

      message.success(template.isActive ? 'Đã ngừng dùng mẫu.' : 'Đã cho dùng lại mẫu.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không đổi được trạng thái.');
    }
  }

  async function incrementVersion(template: ProjectTemplate): Promise<void> {
    try {
      await incrementProjectTemplateVersion(template.id);

      message.success(`Đã lên phiên bản ${String(template.versionNumber + 1)}.`);
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lên phiên bản được.');
    }
  }

  const columns: TableColumnsType<ProjectTemplate> = [
    {
      title: 'Mã mẫu',
      dataIndex: 'code',
      width: 170,
      render: (code: string, template) => (
        <Space size={4}>
          <Typography.Text strong>{code}</Typography.Text>
          {template.isDefault ? (
            <Tooltip title="Mẫu mặc định của loại dự án này.">
              <StarFilled style={{ color: BrandColors.gold }} />
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Tên mẫu',
      dataIndex: 'name',
      render: (name: string, template) => (
        <Space direction="vertical" size={0}>
          <span>{name}</span>
          {template.description !== null && template.description !== '' ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {template.description}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Loại dự án',
      dataIndex: 'projectTypeName',
      width: 190,
      render: (projectTypeName: string | null) => projectTypeName ?? '—',
    },
    {
      title: 'Số việc',
      dataIndex: 'taskCount',
      width: 110,
      align: 'right',
      /*
        Mẫu chưa có việc nào thì áp vào dự án cũng không sinh ra gì. Tô đỏ con số 0 để người khai mẫu
        thấy ngay mẫu nào mới khai xong mà chưa dựng cây công việc.
      */
      render: (taskCount: number) =>
        taskCount === 0 ? (
          <Tooltip title="Mẫu chưa có việc nào nên chưa áp được.">
            <Typography.Text type="danger">0</Typography.Text>
          </Tooltip>
        ) : (
          taskCount
        ),
    },
    {
      title: 'Thời lượng',
      dataIndex: 'estimatedDurationMonths',
      width: 120,
      align: 'right',
      render: (months: number | null) => (months === null ? '—' : `${String(months)} tháng`),
    },
    {
      title: 'Phiên bản',
      dataIndex: 'versionNumber',
      width: 110,
      align: 'right',
      render: (versionNumber: number) => <Tag>v{versionNumber}</Tag>,
    },
    {
      title: 'Còn dùng',
      dataIndex: 'isActive',
      width: 110,
      align: 'center',
      render: (isActive: boolean, template) => (
        <Switch
          size="small"
          checked={isActive}
          disabled={!canManage}
          onChange={() => {
            void toggleActive(template);
          }}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 170,
      render: (_: unknown, template) => (
        <Space size={2}>
          <Tooltip title="Dựng cây công việc trong mẫu">
            <Button
              size="small"
              icon={<ApartmentOutlined />}
              onClick={() => {
                void navigate(`${AppRoutes.ProjectTemplate}/${template.id}`);
              }}
            />
          </Tooltip>
          <Tooltip title="Sửa khai báo">
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={!canManage}
              onClick={() => {
                openEdit(template);
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              template.isActive
                ? 'Đặt hoặc bỏ mẫu mặc định của loại dự án này'
                : 'Mẫu đã ngừng dùng thì không làm mặc định được.'
            }
          >
            <Button
              size="small"
              icon={template.isDefault ? <StarFilled /> : <StarOutlined />}
              disabled={!canManage || !template.isActive}
              onClick={() => {
                void toggleDefault(template);
              }}
            />
          </Tooltip>
          <Tooltip title="Lên một phiên bản — dùng khi quy trình đã đổi thật">
            <Button
              size="small"
              icon={<TagOutlined />}
              disabled={!canManage}
              onClick={() => {
                void incrementVersion(template);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Space direction="vertical" size={0}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Mẫu dự án
          </Typography.Title>
          <Typography.Text type="secondary">
            Quy trình chuẩn của công ty. Mở dự án mới thì áp mẫu để có sẵn cây công việc, danh sách
            kiểm và quan hệ phụ thuộc.
          </Typography.Text>
        </Space>
        <Space wrap>
          <Select<string | null>
            allowClear
            placeholder="Lọc theo loại dự án"
            style={{ minWidth: 200 }}
            value={projectTypeFilter}
            onChange={(value) => {
              setProjectTypeFilter(value ?? null);
            }}
            options={(loaded?.projectTypes ?? []).map((projectType) => ({
              value: projectType.id,
              label: projectType.name,
            }))}
          />
          <Tooltip title="Chỉ hiện mẫu còn dùng">
            <Switch
              checkedChildren="Còn dùng"
              unCheckedChildren="Tất cả"
              checked={onlyActive}
              onChange={setOnlyActive}
            />
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={reload}>
            Tải lại
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canManage}
            onClick={openCreate}
          >
            Khai mẫu mới
          </Button>
        </Space>
      </Space>

      {errorMessage === null ? null : (
        <Alert type="error" showIcon message={errorMessage} />
      )}

      <Table<ProjectTemplate>
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={false}
        columns={columns}
        dataSource={loaded?.items ?? []}
        locale={{
          emptyText:
            'Chưa có mẫu nào. Khai một mẫu cho loại dự án hay gặp nhất trước — thường là loại đã làm nhiều lần nhất.',
        }}
      />

      <Modal
        open={isFormOpen}
        title={editing === null ? 'Khai mẫu dự án mới' : `Sửa mẫu ${editing.code}`}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => {
          setIsFormOpen(false);
        }}
        onOk={() => {
          form.submit();
        }}
      >
        <Form form={form} layout="vertical" onFinish={(values) => void submit(values)}>
          <Form.Item
            name="code"
            label="Mã mẫu"
            tooltip="Được viết hoa tự động. Ví dụ: AOI-CHUAN."
            rules={[{ required: true, message: 'Nhập mã mẫu.' }, { max: 30 }]}
          >
            <Input placeholder="AOI-CHUAN" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Tên mẫu"
            rules={[{ required: true, message: 'Nhập tên mẫu.' }, { max: 200 }]}
          >
            <Input placeholder="Dự án AOI tiêu chuẩn" />
          </Form.Item>
          <Form.Item
            name="projectTypeId"
            label="Loại dự án"
            /*
              Loại dự án của mẫu KHÔNG đổi được sau khi khai: mẫu đã áp cho dự án nào thì cây công
              việc của những dự án đó sinh ra từ nó, và đổi loại sẽ làm phép kiểm "mẫu có đúng loại
              không" lúc áp mẫu nói khác đi so với lúc đã áp.
            */
            tooltip={
              editing === null
                ? 'Mẫu chỉ áp được cho dự án cùng loại.'
                : 'Không đổi được loại dự án của mẫu đã khai.'
            }
            rules={[{ required: true, message: 'Chọn loại dự án.' }]}
          >
            <Select
              disabled={editing !== null}
              placeholder="Chọn loại dự án"
              options={(loaded?.projectTypes ?? [])
                .filter((projectType) => projectType.isActive)
                .map((projectType) => ({
                  value: projectType.id,
                  label: `${projectType.code} — ${projectType.name}`,
                }))}
            />
          </Form.Item>
          <Form.Item name="description" label="Mô tả" rules={[{ max: 2000 }]}>
            <Input.TextArea
              rows={3}
              placeholder="Mẫu này dùng cho những dự án nào, và khác gì so với mẫu còn lại."
            />
          </Form.Item>
          <Form.Item
            name="estimatedDurationMonths"
            label="Thời lượng dự kiến (tháng)"
            tooltip="Chỉ để tham khảo lúc chọn mẫu. Không sinh ra ngày tháng nào cho dự án."
          >
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
